import { RightOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import styled from 'styled-components';
import React from 'react';
import { FacetFilterInput, FacetMetadata } from '../../../types.generated';
import { useAccessibility } from '../../../AccessibilityHandler';
import { capitalizeFirstLetterOnly } from '../../shared/textUtil';
import OptionsDropdownMenu from './OptionsDropdownMenu';
import useSearchFilterDropdown from './useSearchFilterDropdown';
import { IconWrapper } from './SearchFilterView';
import { getFilterDropdownIcon, manageMoreFilterAccessibility } from './utils';
import { MoreFilterOptionLabel } from './styledComponents';

const IconNameWrapper = styled.span`
    display: flex;
    align-items: center;
`;

interface Props {
    filter: FacetMetadata;
    activeFilters: FacetFilterInput[];
    onChangeFilters: (newFilters: FacetFilterInput[]) => void;
}

export default function MoreFilterOption({ filter, activeFilters, onChangeFilters }: Props) {
    const {
        isMenuOpen,
        updateIsMenuOpen,
        updateFilters,
        filterOptions,
        numActiveFilters,
        areFiltersLoading,
        searchQuery,
        updateSearchQuery,
    } = useSearchFilterDropdown({
        filter,
        activeFilters,
        onChangeFilters,
    });
    const filterIcon = getFilterDropdownIcon(filter.field);
    const isAccessibilityEnabled: boolean = useAccessibility();

    function handleBlur(dropdownId) {
        // When a dropdown loses focus, close it after a delay
        if (isAccessibilityEnabled) {
            setTimeout(() => {
                const { activeElement } = document;
                const dropdownElement = document.getElementById(dropdownId);
                const checkToUpdateIsMenuOpen =
                    dropdownElement && !dropdownElement.contains(activeElement) && activeElement?.id !== dropdownId;

                if (checkToUpdateIsMenuOpen) {
                    updateIsMenuOpen(false);
                }
            }, 100); // Small delay to ensure focus hasn't moved back within the dropdown
        }
    }

    return (
        <Dropdown
            trigger={['click']}
            menu={{ items: filterOptions }}
            open={isMenuOpen}
            onOpenChange={(open) => updateIsMenuOpen(open)}
            dropdownRender={(menu) => (
                <OptionsDropdownMenu
                    menu={menu}
                    field={filter.field}
                    updateFilters={updateFilters}
                    searchQuery={searchQuery}
                    updateSearchQuery={updateSearchQuery}
                    isLoading={areFiltersLoading}
                    searchPlaceholder={filter.displayName || ''}
                    alignRight
                />
            )}
        >
            <MoreFilterOptionLabel
                onClick={() => updateIsMenuOpen(!isMenuOpen)}
                isActive={!!numActiveFilters}
                isOpen={isMenuOpen}
                onBlur={() => handleBlur(filter.field)}
                tabIndex={0}
                id="more-filter"
                key={filter.field}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        updateIsMenuOpen(!isMenuOpen);
                    } else {
                        manageMoreFilterAccessibility(e);
                    }
                }}
                className={numActiveFilters ? 'f-color-high-contrast' : ''}
                data-testid={`more-filter-${capitalizeFirstLetterOnly(filter.displayName)}`}
            >
                <IconNameWrapper>
                    {filterIcon && <IconWrapper>{filterIcon}</IconWrapper>}
                    {capitalizeFirstLetterOnly(filter.displayName)} {numActiveFilters ? `(${numActiveFilters}) ` : ''}
                </IconNameWrapper>
                <RightOutlined style={{ fontSize: '12px', height: '12px' }} />
            </MoreFilterOptionLabel>
        </Dropdown>
    );
}
